/**
 * Check if moving a folder would create a circular reference.
 * A folder cannot be moved into itself or any of its descendants.
 *
 * @param {Array} folders - Nested array of folder objects with subfolders
 * @param {number} folderId - The folder being moved
 * @param {number|null} targetParentId - The target parent folder (null = root)
 * @returns {boolean} True if move would create circular reference
 */
export const wouldCreateCircularReference = (
  folders,
  folderId,
  targetParentId,
) => {
  // Moving to root is always valid
  if (targetParentId === null) return false;

  // Can't drop on itself
  if (folderId === targetParentId) return true;

  // Get all descendant IDs of the folder being moved
  const getDescendantIds = (foldersArray, parentId) => {
    const descendants = [];

    const findDescendants = (id) => {
      const findInArray = (arr) => {
        for (const folder of arr) {
          if (folder.folder_id === id) {
            // Found the folder, collect all its descendants
            const collectAll = (f) => {
              if (f.subfolders) {
                for (const sub of f.subfolders) {
                  descendants.push(sub.folder_id);
                  collectAll(sub);
                }
              }
            };
            collectAll(folder);
            return true;
          }
          if (folder.subfolders && findInArray(folder.subfolders)) {
            return true;
          }
        }
        return false;
      };
      findInArray(foldersArray);
    };

    findDescendants(parentId);
    return descendants;
  };

  const descendantIds = getDescendantIds(folders, folderId);
  return descendantIds.includes(targetParentId);
};

/**
 * Remove a folder from a nested folder tree (immutably)
 *
 * @param {Array} folders - Nested array of folder objects
 * @param {number} folderId - The folder ID to remove
 * @returns {Array} New folder tree without the specified folder
 */
export const removeFolderFromTree = (folders, folderId) => {
  return folders
    .filter((f) => f.folder_id !== folderId)
    .map((f) => ({
      ...f,
      subfolders: f.subfolders
        ? removeFolderFromTree(f.subfolders, folderId)
        : [],
    }));
};

/**
 * Find a folder by ID in a nested tree
 *
 * @param {Array} folders - Nested array of folder objects
 * @param {number} folderId - The folder ID to find
 * @returns {Object|null} The folder object or null if not found
 */
export const findFolderById = (folders, folderId) => {
  for (const folder of folders) {
    if (folder.folder_id === folderId) return folder;
    if (folder.subfolders?.length > 0) {
      const found = findFolderById(folder.subfolders, folderId);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Flatten a nested folder tree into a single array with depth info
 *
 * @param {Array} folders - Nested array of folder objects
 * @param {number} depth - Current depth (default 0)
 * @returns {Array} Flat array of folders with depth property
 */
export const flattenFolders = (folders, depth = 0) => {
  let result = [];
  for (const folder of folders) {
    result.push({ ...folder, depth });
    if (folder.subfolders?.length > 0) {
      result = result.concat(flattenFolders(folder.subfolders, depth + 1));
    }
  }
  return result;
};

/**
 * Build the breadcrumb path from root to a specific folder
 *
 * @param {Array} folders - Nested array of folder objects
 * @param {number} targetFolderId - The folder ID to build path to
 * @returns {Array} Array of folder objects from root to target
 */
export const buildBreadcrumbPath = (folders, targetFolderId) => {
  if (targetFolderId === null) return [];

  const path = [];

  const findPath = (foldersArray, target, currentPath) => {
    for (const folder of foldersArray) {
      const newPath = [
        ...currentPath,
        { folder_id: folder.folder_id, name: folder.name },
      ];

      if (folder.folder_id === target) {
        path.push(...newPath);
        return true;
      }

      if (folder.subfolders?.length > 0) {
        if (findPath(folder.subfolders, target, newPath)) {
          return true;
        }
      }
    }
    return false;
  };

  findPath(folders, targetFolderId, []);
  return path;
};
